import styled, { css } from 'styled-components';
import ga from '../../utils/gaConnect';
import { noTextSelect } from './CssCommon';
import {
  removeFile,
  saveFile,
  useIndexedDBDispatch,
  useIndexedDBState
} from '../../context/indexedDBContext';
import Button from './Button';
import { getFormatImg, isDevicePC, useGetHomeURLWithProductType } from '../../utils/util';
import { DOC_FORMAT_CONFIG_VALUES, EDIT_FROM, STARTMODE_WHEN_ROUTING } from '../../define/global';
import trashIcon from '../../img/ico_trash.png';
import trashIcon2x from '../../img/ico_trash@2x.png';
import { useGenConfirmPromise } from '../confirm/Confirm';
import { useTranslation } from 'react-i18next';
import IconNaverWorks from '../../img/ico_works_24.png';
import IconNaverWorks2x from '../../img/ico_works_24@2x.png';
import { getNaverWorksFile, reauthorizationNaverWorks } from '../../extApis/naverWorksApi';
import { useAppDataSliceSelector } from '../../store/modules/appData';
import { useDispatch } from 'react-redux';
import { hideNaverWorksPopup, showNaverWorksPopup } from '../../store/modules/naverWorksPopupData';
import { useWhaleSpaceDataSliceSelector } from '../../store/modules/whaleSpaceData';

const ButtonWrapper = styled.div`
  display: none;
  margin-right: 10px;
  margin-left: auto;
`;

const NaverWorksIcon = styled.img`
  width: 24px;
  height: 24px;
  margin-right: 10px;
  margin-left: auto;
`;

const RemoveButton = css`
  border: none;
  min-width: 24px;
  width: 24px;
  height: 24px;
  padding: 0;
  background-color: transparent;
  &:hover {
    background: inherit;
  }
  &:active {
    background: inherit;
  }
`;

const RecentItemContainer = styled.li`
  display: flex;
  align-items: center;
  //margin-bottom: 6px;
  padding-top: 3px;
  padding-bottom: 3px;
  width: 100%;
  &:hover {
    background-color: #f7f7f7;
    cursor: pointer;
  }
  &:active {
    background-color: #f0f0f0;
    cursor: pointer;
  }

  &:hover ${ButtonWrapper} {
    display: block;
  }

  &:hover ${NaverWorksIcon} {
    display: none;
  }

  ${noTextSelect};
`;

const RecentInfoWapper = styled.div`
  overflow-x: hidden;
  margin-left: 11px;
`;

const FormatImage = styled.img`
  width: 32px;
  height: 32px;
`;

const Title = styled.div`
  width: 100%;
  height: 20px;
  padding-right: 10px;
  font-size: 14px;
  font-weight: 400;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  color: #2f3133;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow-x: hidden;
`;

const DateStyle = styled.div`
  width: 100%;
  height: 18px;
  font-size: 12px;
  font-weight: 400;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  color: #aaadb0;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow-x: hidden;
`;

function RecentItem({ fileInfo, openDocument, isFrom }) {
  const appDataSlice = useAppDataSliceSelector();
  const indexedDBState = useIndexedDBState();
  const indexedDBDispatch = useIndexedDBDispatch();
  const whaleSpaceDataSlice = useWhaleSpaceDataSliceSelector();
  const getHomeURLWithProductType = useGetHomeURLWithProductType();
  const dispatch = useDispatch();
  const confirmPromise = useGenConfirmPromise();
  const { t } = useTranslation();
  const isPCView = isDevicePC(appDataSlice.deviceType);

  const {
    file: { name },
    updateTime,
    isNaverWorksFile,
    naverWorksFileInfo
  } = fileInfo;
  const openTime = new Date(updateTime).toLocaleString();

  const reAuthNOpenNaverWorksDocument = async (isFrom) => {
    await saveFile({
      state: indexedDBState,
      dispatch: indexedDBDispatch,
      file: fileInfo.file,
      isNaverWorksFile: fileInfo.isNaverWorksFile,
      naverWorksFileInfo: fileInfo.naverWorksFileInfo
    });

    reauthorizationNaverWorks({
      domain: whaleSpaceDataSlice.domain,
      isSelf: isFrom === EDIT_FROM.FROM_OPENDIALOG,
      redirectUrl: getHomeURLWithProductType(
        DOC_FORMAT_CONFIG_VALUES.sdk.documnetType,
        STARTMODE_WHEN_ROUTING.OPEN_MOST_RECENT_DOC
      )
    });
  };

  const openRecentFile = async () => {
    try {
      let file = fileInfo.file;
      if (isNaverWorksFile) {
        dispatch(
          showNaverWorksPopup({
            messageKey: 'NaverWorksFileNavigator.DocumentProgressPopupMessage',
            isError: false,
            isMobile: isPCView === false
          })
        );
        const worksFileData = await getNaverWorksFile(
          naverWorksFileInfo.driveType,
          naverWorksFileInfo.driveId,
          naverWorksFileInfo.fileId
        );
        if (worksFileData.file) file = worksFileData.file;
      }

      await openDocument(
        [file],
        (executeTime, fileBlob) => {
          ga.event({
            category: '문서열기',
            action: '최근문서',
            label: '시작화면',
            value: executeTime
          });
          ga.event({
            category: '사용지표',
            action: '열람 문서 크기',
            label: '최근문서',
            value: fileBlob.size
          });
        },
        isNaverWorksFile,
        naverWorksFileInfo
      );
      dispatch(hideNaverWorksPopup());
    } catch (error) {
      if (error.code === 401) {
        dispatch(hideNaverWorksPopup());
        const confirm = await confirmPromise({
          message: t('NaverWorksFileNavigator.ReauthMessage'),
          btn: {
            confirm: {
              text: t('Common.Connect')
            },
            cancel: {
              text: t('Common.Cancel')
            }
          }
        });
        if (confirm) {
          await reAuthNOpenNaverWorksDocument(isFrom);
        }
      } else {
        const errorMessageKey =
          error.code === 403
            ? 'NaverWorksFileNavigator.ForbiddenMessage'
            : 'NaverWorksFileNavigator.InternalErrorMessage';

        dispatch(
          showNaverWorksPopup({
            messageKey: errorMessageKey,
            isError: true,
            errorCode: error.code === 403 ? null : error.code,
            isMobile: isPCView === false
          })
        );
      }
    }
  };

  const deleteRecentFile = async (event) => {
    event.stopPropagation();
    const confirm = await confirmPromise({ message: t('Common.removeRecentItem') });
    if (confirm) {
      removeFile({ state: indexedDBState, dispatch: indexedDBDispatch, file: fileInfo.file });
    }
  };
  const { formatImg, formatImg2x } = getFormatImg(DOC_FORMAT_CONFIG_VALUES.sdk.documnetType);

  return (
    <RecentItemContainer onClick={openRecentFile}>
      <FormatImage src={formatImg} srcSet={formatImg2x} />
      <RecentInfoWapper>
        <Title>{name}</Title>
        <DateStyle>{openTime}</DateStyle>
      </RecentInfoWapper>
      {isNaverWorksFile && <NaverWorksIcon src={IconNaverWorks} srcSet={IconNaverWorks2x} />}
      <ButtonWrapper>
        <Button cssExt={RemoveButton} onClick={deleteRecentFile}>
          <img src={trashIcon} srcSet={trashIcon2x} width="24px" height="24px" alt="trashIcon" />
        </Button>
      </ButtonWrapper>
    </RecentItemContainer>
  );
}

export default RecentItem;
